import React, { useEffect, useRef, useState } from 'react';
import CropUploader from '@mysimpledb/shared/components/UI/CropUploader';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import PhoneNumberField, {
  normalizePhoneForSave,
} from '../PhoneNumberField/PhoneNumberField';
import axios from '@mysimpledb/shared/config/axios';

const CDN_BASE = process.env.REACT_APP_MEDIA_CDN_BASE_URL;
const API_URL = process.env.REACT_APP_API_URL || process.env.API_URL || '';
const EMAIL_AVAILABLE_ENDPOINTS = [
  '/auth/email-available',
  '/users/check-email',
];
const VALIDATE_PHONE_ENDPOINT = '/twilio/validate-phone';
const SEND_PHONE_VERIFICATION_ENDPOINT = '/twilio/phone-verification/send';
const CHECK_PHONE_VERIFICATION_ENDPOINT = '/twilio/phone-verification/check';

function normalizeEmail(email) {
  return String(email || '')
    .trim()
    .toLowerCase();
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalizeEmail(email));
}

function normalizePhone(phone, phoneCountry) {
  const rawPhone = String(phone || '').trim();
  if (!rawPhone) return null;

  try {
    return normalizePhoneForSave(rawPhone, phoneCountry || null) || null;
  } catch {
    return null;
  }
}

async function checkEmailAvailable(email) {
  for (const endpoint of EMAIL_AVAILABLE_ENDPOINTS) {
    try {
      const response = await axios.get(endpoint, {
        params: { email: normalizeEmail(email) },
      });

      return {
        ok: true,
        available: Boolean(response?.data?.available),
      };
    } catch (error) {
      if (error?.response?.status === 404) continue;

      return {
        ok: false,
        available: null,
        error,
      };
    }
  }

  return {
    ok: false,
    available: null,
  };
}

const stripTrailingSlash = (value = '') =>
  String(value || '').replace(/\/+$/, '');
const stripLeadingSlash = (value = '') =>
  String(value || '').replace(/^\/+/, '');

const appendCacheBust = (url, cacheBust = 0) => {
  if (!cacheBust) return url;
  return `${url}${url.includes('?') ? '&' : '?'}t=${cacheBust}`;
};

const isAbsoluteUrl = (value = '') =>
  /^(https?:\/\/|blob:|data:)/i.test(String(value).trim());

function getApiOrigin() {
  if (API_URL) {
    try {
      return new URL(API_URL).origin;
    } catch {
      // ignore and continue to window-based fallback
    }
  }

  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname } = window.location;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5001';
    }

    return `${protocol}//${hostname}:5001`;
  }

  return 'http://localhost:5001';
}

function buildAvatarUrl(profilePicture, cacheBust = 0) {
  if (!profilePicture) return '/images/default-user-profile.png';

  const raw = String(profilePicture).trim();
  if (!raw) return '/images/default-user-profile.png';

  if (isAbsoluteUrl(raw)) {
    return appendCacheBust(raw, cacheBust);
  }

  const normalized = stripLeadingSlash(raw);

  // New preferred format: storage key such as "avatars/12-avatar-123.jpg"
  if (normalized.includes('/')) {
    if (CDN_BASE) {
      return appendCacheBust(
        `${stripTrailingSlash(CDN_BASE)}/${normalized}`,
        cacheBust
      );
    }

    return appendCacheBust(
      `${stripTrailingSlash(getApiOrigin())}/${normalized}`,
      cacheBust
    );
  }

  // Legacy fallback: older DB values that stored just the filename
  if (CDN_BASE) {
    return appendCacheBust(
      `${stripTrailingSlash(CDN_BASE)}/avatars/${normalized}`,
      cacheBust
    );
  }

  return appendCacheBust(
    `${stripTrailingSlash(getApiOrigin())}/uploads/users/${normalized}`,
    cacheBust
  );
}

const PersonInfo = ({
  data,
  types = [],
  initialTypeIds = [],
  initialSubtypeIds = [],
  errors = {},
  loading = false,
  profilePicture,
  cacheBust = 0,
  onChange,
  onSave,
  onProfilePictureUpdate,
}) => {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [typeIds, setTypeIds] = useState(new Set(initialTypeIds));
  const [subtypeIds, setSubtypeIds] = useState(new Set(initialSubtypeIds));
  const [draft, setDraft] = useState(null);
  const [draftUrl, setDraftUrl] = useState(null);
  const [smsNotice, setSmsNotice] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailStatus, setEmailStatus] = useState('');
  const [lastCheckedEmail, setLastCheckedEmail] = useState('');
  const originalEmailRef = useRef(null);
  const emailCheckSequenceRef = useRef(0);
  const [originalPhoneE164, setOriginalPhoneE164] = useState(null);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [phoneValidationLoading, setPhoneValidationLoading] = useState(false);
  const [phoneVerificationLoading, setPhoneVerificationLoading] =
    useState(false);
  const [phoneVerificationSent, setPhoneVerificationSent] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhoneE164, setVerifiedPhoneE164] = useState('');
  const [phoneVerificationError, setPhoneVerificationError] = useState('');
  const [phoneVerificationMessage, setPhoneVerificationMessage] = useState('');
  const [phoneAvailabilityChecking, setPhoneAvailabilityChecking] =
    useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null);
  const [lastCheckedPhoneE164, setLastCheckedPhoneE164] = useState('');
  const [phoneAvailabilityError, setPhoneAvailabilityError] = useState('');
  const phoneCheckSequenceRef = useRef(0);
  const phoneValidationTimerRef = useRef(null);

  function getSmsStatusMessage(status, smsEnabled) {
    switch (status) {
      case 'enabled':
        return 'Profile saved. SMS notifications are enabled.';

      case 'disabled':
        return 'Profile saved. SMS notifications are disabled.';

      case 'invalid_phone':
        return 'Profile saved. SMS notifications were disabled because the phone number is incomplete or invalid.';

      case 'missing_phone':
        return 'Profile saved. SMS notifications were not enabled because a phone number is required.';

      case 'phone_already_used':
        return 'The mobile phone number you entered is already associated with another MySimpleDB account. Please use a different mobile phone number or disable SMS notifications.';

      default:
        return 'Profile saved.';
    }
  }

  useEffect(() => {
    if (!loading) {
      setHasLoadedOnce(true);

      const loadedEmail = normalizeEmail(data.email);

      // PersonInfo can render once with empty data before the account request
      // finishes. Do not capture that empty value as the account's original
      // email; wait until the real email has arrived.
      if (!originalEmailRef.current && loadedEmail) {
        originalEmailRef.current = loadedEmail;
      }
    }
  }, [data.email, loading]);

  useEffect(() => {
    // A loaded email tells us the account data has arrived. Capture the
    // original normalized phone once; an empty string means no saved phone.
    if (originalPhoneE164 === null && normalizeEmail(data.email)) {
      setOriginalPhoneE164(
        normalizePhone(
          data.phone ?? data.phone_number,
          data.phoneCountry ?? data.phone_country
        ) || ''
      );
    }
  }, [
    data.email,
    data.phone,
    data.phone_number,
    data.phoneCountry,
    data.phone_country,
    originalPhoneE164,
  ]);

  useEffect(() => {
    setTypeIds(new Set((initialTypeIds || []).map(Number)));
  }, [initialTypeIds]);

  useEffect(() => {
    setSubtypeIds(new Set((initialSubtypeIds || []).map(Number)));
  }, [initialSubtypeIds]);

  useEffect(() => {
    return () => {
      if (draftUrl) {
        URL.revokeObjectURL(draftUrl);
      }
    };
  }, [draftUrl]);

  useEffect(() => {
    return () => {
      if (phoneValidationTimerRef.current) {
        clearTimeout(phoneValidationTimerRef.current);
      }
    };
  }, []);

  const rawCurrentPhone = String(data.phone ?? data.phone_number ?? '').trim();
  const currentPhoneCountry = data.phoneCountry ?? data.phone_country ?? null;
  const currentPhoneE164 = normalizePhone(rawCurrentPhone, currentPhoneCountry);
  const requestedSmsEnabled = Boolean(
    data.smsEnabled ?? data.sms_enabled ?? false
  );
  const phoneInputInvalid = Boolean(rawCurrentPhone) && !currentPhoneE164;
  const phoneChanged =
    originalPhoneE164 !== null &&
    (currentPhoneE164 || '') !== originalPhoneE164;
  const phoneNeedsVerification = phoneInputInvalid || phoneChanged;
  const changedPhoneRequiresVerification =
    requestedSmsEnabled &&
    phoneNeedsVerification &&
    (!phoneVerified ||
      !phoneVerificationToken ||
      !currentPhoneE164 ||
      verifiedPhoneE164 !== currentPhoneE164);

  const showInitialLoading = loading && !hasLoadedOnce;
  if (showInitialLoading) {
    return <p>Loading...</p>;
  }

  const toggleType = (id) => {
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) return;

    setTypeIds((prev) => {
      const next = new Set(prev);

      if (next.has(n)) {
        next.delete(n);

        const t = types.find((x) => x.id === n);
        if (t?.subtypes?.length) {
          const nextSubs = new Set(subtypeIds);
          t.subtypes.forEach((st) => nextSubs.delete(st.id));
          setSubtypeIds(nextSubs);
        }
      } else {
        next.add(n);
      }

      return next;
    });
  };

  const toggleSubtype = (typeId, subId) => {
    setSubtypeIds((prev) => {
      const next = new Set(prev);

      if (next.has(subId)) {
        next.delete(subId);
      } else {
        next.add(subId);

        if (!typeIds.has(typeId)) {
          const nextTypes = new Set(typeIds);
          nextTypes.add(typeId);
          setTypeIds(nextTypes);
        }
      }

      return next;
    });
  };

  const selectAllInType = (typeId, checked) => {
    const t = types.find((x) => x.id === typeId);
    const nextTypes = new Set(typeIds);
    const nextSubs = new Set(subtypeIds);

    if (checked) {
      nextTypes.add(typeId);
      t?.subtypes?.forEach((s) => nextSubs.add(s.id));
    } else {
      nextTypes.delete(typeId);
      t?.subtypes?.forEach((s) => nextSubs.delete(s.id));
    }

    setTypeIds(nextTypes);
    setSubtypeIds(nextSubs);
  };

  const handleDraftPhoto = ({ blob, name }) => {
    if (draftUrl) URL.revokeObjectURL(draftUrl);
    setDraft({ blob, name });
    setDraftUrl(URL.createObjectURL(blob));
  };

  const validateEmail = async () => {
    const email = normalizeEmail(data.email);
    const checkSequence = ++emailCheckSequenceRef.current;

    setEmailStatus('');
    setEmailAvailable(null);
    setLastCheckedEmail(email);

    if (!isValidEmail(email)) {
      setEmailAvailable(false);
      setEmailStatus('Please enter a valid email address.');
      return false;
    }

    // The availability endpoint correctly reports the current account's email
    // as already registered. It is nevertheless valid when it has not changed.
    if (email === originalEmailRef.current) {
      setEmailAvailable(true);
      return true;
    }

    setEmailChecking(true);

    try {
      const availability = await checkEmailAvailable(email);

      // Ignore a response for an email the user changed while this request ran.
      if (
        checkSequence !== emailCheckSequenceRef.current ||
        email !== normalizeEmail(data.email)
      ) {
        return false;
      }

      if (availability.ok && availability.available === false) {
        setEmailAvailable(false);
        setEmailStatus(
          'That email is already registered. Please use a different email.'
        );
        return false;
      }

      if (availability.ok && availability.available === true) {
        setEmailAvailable(true);
        return true;
      }

      // The update endpoint/database remains the final duplicate-email guard.
      // Do not block an otherwise valid email if the pre-check is unavailable.
      setEmailAvailable(null);
      return true;
    } finally {
      if (checkSequence === emailCheckSequenceRef.current) {
        setEmailChecking(false);
      }
    }
  };

  const handleEmailChange = (value) => {
    emailCheckSequenceRef.current += 1;
    setEmailChecking(false);
    setEmailAvailable(null);
    setEmailStatus('');
    setLastCheckedEmail('');
    onChange('email', value);
  };

  const resetPhoneVerification = () => {
    setPhoneVerificationSent(false);
    setPhoneVerificationCode('');
    setPhoneVerified(false);
    setVerifiedPhoneE164('');
    setPhoneVerificationToken('');
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');
  };

  const handlePhoneChange = (value) => {
    setSmsNotice('');
    resetPhoneVerification();
    phoneCheckSequenceRef.current += 1;
    clearTimeout(phoneValidationTimerRef.current);
    setPhoneAvailabilityChecking(false);
    setPhoneAvailable(null);
    setLastCheckedPhoneE164('');
    setPhoneAvailabilityError('');
    onChange('phone', value);
    onChange('phone_number', value);

    const rawPhone = String(value || '').trim();
    const phoneE164 = normalizePhone(rawPhone, currentPhoneCountry);
    const inputInvalid = Boolean(rawPhone) && !phoneE164;
    const changed =
      originalPhoneE164 !== null &&
      (phoneE164 || '') !== originalPhoneE164;

    if (requestedSmsEnabled && (rawPhone || changed)) {
      phoneValidationTimerRef.current = setTimeout(() => {
        validatePhoneAvailability({ phoneE164, changed, inputInvalid });
      }, 700);
    }
  };

  const handlePhoneCountryChange = (value) => {
    setSmsNotice('');
    resetPhoneVerification();
    phoneCheckSequenceRef.current += 1;
    clearTimeout(phoneValidationTimerRef.current);
    setPhoneAvailabilityChecking(false);
    setPhoneAvailable(null);
    setLastCheckedPhoneE164('');
    setPhoneAvailabilityError('');
    onChange('phoneCountry', value);
    onChange('phone_country', value);

    const phoneE164 = normalizePhone(rawCurrentPhone, value);
    const inputInvalid = Boolean(rawCurrentPhone) && !phoneE164;
    const changed =
      originalPhoneE164 !== null &&
      (phoneE164 || '') !== originalPhoneE164;

    if (requestedSmsEnabled && (rawCurrentPhone || changed)) {
      phoneValidationTimerRef.current = setTimeout(() => {
        validatePhoneAvailability({ phoneE164, changed, inputInvalid });
      }, 700);
    }
  };

  const handleSmsEnabledChange = (checked) => {
    setSmsNotice('');
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');

    if (!checked) {
      resetPhoneVerification();
      phoneCheckSequenceRef.current += 1;
      clearTimeout(phoneValidationTimerRef.current);
      setPhoneAvailabilityChecking(false);
      setPhoneAvailable(null);
      setLastCheckedPhoneE164('');
      setPhoneAvailabilityError('');
    } else if (!currentPhoneE164) {
      setPhoneAvailable(false);
      setLastCheckedPhoneE164('');
      setPhoneAvailabilityError('Please enter a valid phone number.');
    } else if (!phoneChanged) {
      setPhoneAvailable(true);
      setLastCheckedPhoneE164(currentPhoneE164);
      setPhoneAvailabilityError('');
    } else {
      clearTimeout(phoneValidationTimerRef.current);
      phoneValidationTimerRef.current = setTimeout(() => {
        validatePhoneAvailability({
          phoneE164: currentPhoneE164,
          changed: phoneChanged,
          inputInvalid: phoneInputInvalid,
        });
      }, 700);
    }

    onChange('smsEnabled', checked);
    onChange('sms_enabled', checked);
  };

  const validatePhoneAvailability = async ({
    phoneE164,
    changed,
    inputInvalid,
  }) => {
    const checkSequence = ++phoneCheckSequenceRef.current;

    setPhoneAvailabilityError('');

    if (inputInvalid || (changed && !phoneE164)) {
      setPhoneAvailable(false);
      setLastCheckedPhoneE164('');
      setPhoneAvailabilityError('Please enter a valid phone number.');
      return false;
    }

    if (!changed) {
      setPhoneAvailable(true);
      setLastCheckedPhoneE164(phoneE164 || '');
      return true;
    }

    setPhoneAvailabilityChecking(true);
    setPhoneAvailable(null);
    setLastCheckedPhoneE164(phoneE164);

    try {
      const response = await axios.post(VALIDATE_PHONE_ENDPOINT, {
        phoneE164,
        checkAvailability: true,
      });

      if (checkSequence !== phoneCheckSequenceRef.current) return false;

      if (!response?.data?.valid) {
        setPhoneAvailable(false);
        setPhoneAvailabilityError('Please enter a valid phone number.');
        return false;
      }

      setPhoneAvailable(true);
      return true;
    } catch (error) {
      if (checkSequence !== phoneCheckSequenceRef.current) return false;

      setPhoneAvailable(false);

      if (error?.response?.data?.error === 'phone_already_used') {
        setPhoneAvailabilityError(
          'That phone number is already associated with another account.'
        );
      } else {
        setPhoneAvailabilityError(
          error?.response?.data?.message ||
            'Unable to validate the phone number. Please try again.'
        );
      }

      return false;
    } finally {
      if (checkSequence === phoneCheckSequenceRef.current) {
        setPhoneAvailabilityChecking(false);
      }
    }
  };

  const validatePhoneOnBlur = () => {
    clearTimeout(phoneValidationTimerRef.current);

    if (!requestedSmsEnabled) {
      setPhoneAvailabilityChecking(false);
      setPhoneAvailable(null);
      setLastCheckedPhoneE164('');
      setPhoneAvailabilityError('');
      return true;
    }

    return validatePhoneAvailability({
      phoneE164: currentPhoneE164,
      changed: phoneChanged,
      inputInvalid: phoneInputInvalid,
    });
  };

  const handlePhoneFieldBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    validatePhoneOnBlur();
  };

  const sendPhoneVerification = async () => {
    setPhoneValidationLoading(true);
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');

    try {
      const normalizedPhone = normalizePhone(
        rawCurrentPhone,
        currentPhoneCountry
      );

      if (!normalizedPhone) {
        setPhoneVerificationError(
          'Please enter a valid phone number before requesting a code.'
        );
        return;
      }

      const validationResponse = await axios.post(VALIDATE_PHONE_ENDPOINT, {
        phoneE164: normalizedPhone,
        checkAvailability: true,
      });

      if (!validationResponse?.data?.valid) {
        setPhoneVerificationError('Please enter a valid phone number.');
        return;
      }

      const phoneE164 = validationResponse.data.phoneE164 || normalizedPhone;

      await axios.post(SEND_PHONE_VERIFICATION_ENDPOINT, {
        phoneE164,
      });

      setVerifiedPhoneE164(phoneE164);
      setPhoneVerificationSent(true);
      setPhoneVerified(false);
      setPhoneVerificationToken('');
      setPhoneVerificationCode('');
      setPhoneVerificationMessage(
        `A verification code was sent to ${
          validationResponse.data.nationalFormat || phoneE164
        }.`
      );
    } catch (error) {
      if (error?.response?.data?.error === 'phone_already_used') {
        setPhoneAvailable(false);
        setLastCheckedPhoneE164(normalizedPhone);
        setPhoneAvailabilityError(
          'That phone number is already associated with another account.'
        );
      } else {
        setPhoneVerificationError(
          error?.response?.data?.message ||
            'Unable to send the verification code. Please try again.'
        );
      }
    } finally {
      setPhoneValidationLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    setPhoneVerificationLoading(true);
    setPhoneVerificationError('');
    setPhoneVerificationMessage('');

    try {
      const code = phoneVerificationCode.trim();

      if (!/^\d{4,10}$/.test(code)) {
        setPhoneVerificationError('Please enter the verification code.');
        return;
      }

      if (!verifiedPhoneE164) {
        setPhoneVerificationError('Please request a new verification code.');
        return;
      }

      const response = await axios.post(CHECK_PHONE_VERIFICATION_ENDPOINT, {
        phoneE164: verifiedPhoneE164,
        code,
      });

      if (!response?.data?.verified) {
        setPhoneVerificationError(
          'The verification code is incorrect or has expired.'
        );
        return;
      }

      const returnedToken = response?.data?.phoneVerificationToken;
      const returnedPhone = response?.data?.phoneE164 || verifiedPhoneE164;

      if (!returnedToken) {
        setPhoneVerified(false);
        setPhoneVerificationToken('');
        setPhoneVerificationError(
          'The server did not return phone verification proof. Please try again.'
        );
        return;
      }

      setVerifiedPhoneE164(returnedPhone);
      setPhoneVerificationToken(returnedToken);
      setPhoneVerified(true);
      setPhoneVerificationMessage('Phone number verified.');
    } catch (error) {
      setPhoneVerified(false);
      setPhoneVerificationToken('');
      setPhoneVerificationError(
        error?.response?.data?.message ||
          'Unable to verify the phone number. Please try again.'
      );
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handleSave = async () => {
    let finalProfilePicture = profilePicture;

    const email = normalizeEmail(data.email);
    const emailAlreadyValidated =
      lastCheckedEmail === email && emailAvailable === true;

    if (!emailAlreadyValidated) {
      const emailIsValidAndAvailable = await validateEmail();
      if (!emailIsValidAndAvailable) return;
    }

    if (changedPhoneRequiresVerification) {
      setPhoneVerificationError(
        currentPhoneE164
          ? 'Please verify the new phone number before saving.'
          : 'Please enter a valid phone number before saving.'
      );
      return;
    }

    try {
      setSmsNotice('');

      const rawPhone = String(data.phone ?? data.phone_number ?? '').trim();
      const currentPhoneCountry =
        data.phoneCountry ?? data.phone_country ?? null;
      const requestedSmsEnabled = Boolean(data.smsEnabled ?? false);

      let normalizedPhone = null;
      let normalizedPhoneCountry = null;
      let finalSmsEnabled = false;
      let nextSmsNotice = '';

      if (rawPhone) {
        normalizedPhone = normalizePhone(rawPhone, currentPhoneCountry);

        normalizedPhoneCountry = currentPhoneCountry?.trim()
          ? currentPhoneCountry.trim().toUpperCase()
          : null;

        if (normalizedPhone && requestedSmsEnabled) {
          finalSmsEnabled = true;
        }

        if (!normalizedPhone) {
          finalSmsEnabled = false;
          nextSmsNotice =
            'Profile saved. SMS notifications were disabled because the phone number is incomplete or invalid.';
        }
      } else if (requestedSmsEnabled) {
        finalSmsEnabled = false;
        nextSmsNotice =
          'Profile saved. SMS notifications were not enabled because a phone number is required.';
      }

      if (draft?.blob) {
        const fd = new FormData();
        fd.append('image', draft.blob, draft.name || 'avatar.jpg');

        const res = await axios.post('/users/upload-avatar', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const json = res?.data || {};

        finalProfilePicture =
          json.storageKey ??
          json.fileKey ??
          json.filePath ??
          json.fileName ??
          json.filename ??
          json.file ??
          finalProfilePicture;

        if (!finalProfilePicture) {
          throw new Error('Upload failed: no filename returned from server');
        }

        onProfilePictureUpdate?.(finalProfilePicture);
      }

      const result = await onSave({
        profilePicture: finalProfilePicture,
        phone: normalizedPhone || rawPhone || null,
        phoneNumber: rawPhone || null,
        phoneE164: normalizedPhone,
        phoneCountry: rawPhone ? normalizedPhoneCountry : null,
        smsEnabled: requestedSmsEnabled,
        phoneVerificationToken:
          requestedSmsEnabled && phoneChanged && phoneVerified
            ? phoneVerificationToken
            : null,
        allowedTypeIds: Array.from(typeIds),
        allowedSubtypeIds: Array.from(subtypeIds),
      });

      originalEmailRef.current = email;
      setLastCheckedEmail(email);
      setEmailAvailable(true);

      const smsStatus = result?.success?.smsStatus;
      const phoneSaveRejected = [
        'invalid_phone',
        'missing_phone',
        'phone_already_used',
      ].includes(smsStatus);

      if (!phoneSaveRejected) {
        setOriginalPhoneE164(normalizedPhone || '');
      }

      // Verification proof is single-use. Clear it after every save attempt.
      resetPhoneVerification();

      nextSmsNotice = getSmsStatusMessage(smsStatus, data.smsEnabled);

      if (nextSmsNotice) {
        setSmsNotice(nextSmsNotice);
      } else if (finalSmsEnabled) {
        setSmsNotice('Profile saved. SMS notifications are enabled.');
      } else {
        setSmsNotice('Profile saved. SMS notifications are disabled.');
      }
    } finally {
      setDraft(null);
    }
  };

  const avatarUrl = draftUrl
    ? draftUrl
    : buildAvatarUrl(profilePicture, cacheBust);

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr minmax(260px, 340px)',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div>
          <label>First Name</label>
          <input
            className="standard-input"
            value={data.firstName || ''}
            onChange={(e) => onChange('firstName', e.target.value)}
          />

          <label>Last Name</label>
          <input
            className="standard-input"
            value={data.lastName || ''}
            onChange={(e) => onChange('lastName', e.target.value)}
          />

          <label>Email</label>
          <input
            type="email"
            className="standard-input"
            value={data.email || ''}
            onChange={(e) => handleEmailChange(e.target.value)}
            onBlur={validateEmail}
            autoComplete="email"
            inputMode="email"
          />

          {emailChecking ? (
            <div style={{ marginTop: -12, marginBottom: 12, fontSize: 13 }}>
              Checking email...
            </div>
          ) : null}

          {!emailChecking && emailStatus ? (
            <div
              role="alert"
              style={{
                marginTop: -12,
                marginBottom: 12,
                fontSize: 13,
                color: '#dc2626',
              }}
            >
              {emailStatus}
            </div>
          ) : null}

          <div onBlur={handlePhoneFieldBlur}>
            <PhoneNumberField
              countryValue={data.phoneCountry ?? ''}
              onCountryChange={handlePhoneCountryChange}
              phoneValue={data.phone ?? ''}
              onPhoneChange={handlePhoneChange}
              disabled={
                phoneValidationLoading ||
                phoneVerificationLoading ||
                phoneVerified
              }
            />
          </div>

          {requestedSmsEnabled && phoneAvailabilityChecking ? (
            <div
              className="field-note"
              role="status"
              style={{ marginTop: 2, marginBottom: 12 }}
            >
              Checking phone number...
            </div>
          ) : null}

          {requestedSmsEnabled &&
          !phoneAvailabilityChecking &&
          phoneAvailabilityError ? (
            <div
              className="email-error"
              role="alert"
              style={{ marginTop: 2, marginBottom: 12 }}
            >
              {phoneAvailabilityError}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 12,
              padding: '12px 14px',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 10,
              background: 'var(--surface-muted, #f9fafb)',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                margin: 0,
                fontWeight: 700,
              }}
            >
              <input
                type="checkbox"
                checked={requestedSmsEnabled}
                onChange={(e) => handleSmsEnabledChange(e.target.checked)}
                disabled={phoneValidationLoading || phoneVerificationLoading}
              />
              Enable SMS notifications
            </label>

            <p
              style={{
                margin: '8px 0 0',
                fontSize: 12,
                lineHeight: 1.4,
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              Receive account-related text messages such as verification codes,
              password resets, sync notices, and optional reminders. Message and
              data rates may apply.
            </p>

            {smsNotice ? (
              <p
                role="status"
                style={{
                  margin: '8px 0 0',
                  fontSize: 12,
                  lineHeight: 1.4,
                  color: '#92400e',
                }}
              >
                {smsNotice}
              </p>
            ) : null}
          </div>

          {requestedSmsEnabled && phoneNeedsVerification ? (
            <div
              className="phone-verification-panel"
              style={{
                marginTop: 12,
                padding: 14,
                border: '1px solid #dbe3ef',
                borderRadius: 10,
                background: '#f7f9fc',
              }}
            >
              <p
                style={{
                  margin: '0 0 12px',
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: '#374151',
                }}
              >
                The phone number has changed. Verify the new number before
                saving your profile.
              </p>

              {!phoneVerificationSent && !phoneVerified ? (
                <AppButton
                  type="button"
                  variant="outline"
                  onPress={sendPhoneVerification}
                  disabled={
                    !currentPhoneE164 ||
                    phoneAvailabilityChecking ||
                    phoneAvailable !== true ||
                    lastCheckedPhoneE164 !== currentPhoneE164 ||
                    phoneValidationLoading ||
                    phoneVerificationLoading
                  }
                >
                  {phoneValidationLoading
                    ? 'Sending code...'
                    : 'Verify phone number'}
                </AppButton>
              ) : null}

              {phoneVerificationSent && !phoneVerified ? (
                <>
                  <label
                    htmlFor="profilePhoneVerificationCode"
                    className="standard-label"
                    style={{ marginTop: 0 }}
                  >
                    Verification Code
                  </label>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <input
                      type="text"
                      id="profilePhoneVerificationCode"
                      className="standard-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={10}
                      value={phoneVerificationCode}
                      onChange={(event) =>
                        setPhoneVerificationCode(
                          event.target.value.replace(/\D/g, '').slice(0, 10)
                        )
                      }
                      disabled={phoneVerificationLoading}
                      placeholder="Enter verification code"
                      style={{ flex: 1, minWidth: 0, margin: 0 }}
                    />

                    <AppButton
                      type="button"
                      variant="solid"
                      onPress={verifyPhoneCode}
                      disabled={
                        phoneVerificationLoading ||
                        phoneVerificationCode.trim().length < 4
                      }
                    >
                      {phoneVerificationLoading
                        ? 'Verifying...'
                        : 'Verify code'}
                    </AppButton>
                  </div>

                  <button
                    type="button"
                    className="form-link"
                    onClick={sendPhoneVerification}
                    disabled={
                      phoneValidationLoading || phoneVerificationLoading
                    }
                    style={{
                      marginTop: 10,
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    Send another code
                  </button>
                </>
              ) : null}

              {phoneVerificationMessage &&
              !phoneVerificationError &&
              !phoneVerified ? (
                <div className="field-note" role="status">
                  {phoneVerificationMessage}
                </div>
              ) : null}

              {phoneVerified ? (
                <div
                  className="field-note success"
                  role="status"
                  style={{ marginTop: 0 }}
                >
                  Phone number verified.
                </div>
              ) : null}

              {phoneVerificationError ? (
                <div className="email-error" role="alert">
                  {phoneVerificationError}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div>
          <label>Profile Picture</label>
          <div className="avatar-circle">
            <img src={avatarUrl} alt="Profile" />
          </div>
          <CropUploader onCropComplete={handleDraftPhoto} />
        </div>
      </div>

      <div
        style={{
          paddingTop: 8,
          borderTop: '1px solid var(--border, #e5e7eb)',
        }}
      >
        <h2 style={{ margin: 0 }}>User Type</h2>
        <p style={{ marginTop: 6, marginBottom: 14 }}>
          Select your user types and any subtypes. These will be used as
          defaults when creating entries and for filtering shared content.
        </p>

        {types.map((t) => {
          const allSubIds = (t.subtypes || []).map((s) => s.id);
          const allSelected =
            allSubIds.length > 0 && allSubIds.every((id) => subtypeIds.has(id));
          const someSelected =
            allSubIds.some((id) => subtypeIds.has(id)) && !allSelected;

          return (
            <div key={t.id} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontWeight: 800, marginTop: '12px' }}>
                  <input
                    type="checkbox"
                    checked={typeIds.has(t.id)}
                    onChange={() => toggleType(t.id)}
                  />{' '}
                  {t.name}
                </label>

                {t.subtypes?.length ? (
                  <label
                    style={{ fontSize: 12, opacity: 0.9, marginTop: '12px' }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={(e) => selectAllInType(t.id, e.target.checked)}
                    />{' '}
                    select all subtypes
                  </label>
                ) : null}
              </div>

              {t.subtypes?.length ? (
                <div
                  style={{
                    marginLeft: 28,
                    marginTop: 8,
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 8,
                  }}
                >
                  {t.subtypes.map((s) => (
                    <label key={s.id}>
                      <input
                        type="checkbox"
                        checked={subtypeIds.has(s.id)}
                        onChange={() => toggleSubtype(t.id, s.id)}
                      />{' '}
                      {s.name}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div>
        <AppButton
          variant="solid"
          onPress={handleSave}
          disabled={
            loading ||
            emailChecking ||
            phoneValidationLoading ||
            phoneVerificationLoading ||
            changedPhoneRequiresVerification ||
            !isValidEmail(data.email) ||
            (emailAvailable === false &&
              lastCheckedEmail === normalizeEmail(data.email))
          }
          style={{ width: 150 }}
        >
          Save
        </AppButton>
      </div>
    </div>
  );
};

export default PersonInfo;
